<template>
  <CRUDDialog
    :baseCrudKey="miscBaseCrudKey"
    @upsertItem="upsertTransaction"
  >
    <template v-slot:[`editedItem`]="{}">
      <TransactionEditedItem
        :baseCrudKey="miscBaseCrudKey"
        @source-updated="handleSourceUpdate"/>
    </template>
  </CRUDDialog>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapActions,
  mapMutations,
} from 'vuex';
import TransactionEditedItem from '../../Misc/Transaction/TransactionEditedItem.vue';
import { GL_CONSTANTS } from '../Transaction/gl-balances.util';
import CRUDDialog from '../../common/base/crud/CRUDDialog.vue';
import { toFiscalYear, createObjectFromArray } from '../../../util/shared/tmc-global';
import { amountToNumber } from '../../../util/shared/vue-global';
import { getWorkstationNumber } from '../../../util/workstation-util';
import { buildPaymentItem } from '../../Misc/Transaction/miscTransaction.util';

export default {
  components: {
    CRUDDialog,
    TransactionEditedItem,
  },
  watch: {
    openMiscEditedItemFlag() {
      this.miscNewItem();
    },
  },
  props: {
    openMiscEditedItemFlag: {
      type: Number,
      default: 0,
    },
    baseCrudKey: {
      type: String,
      default: '',
    },
    interestReceived: {
      type: Number,
      default: 0.0,
    },
  },
  data: () => ({
    miscBaseCrudKey: 'miscTransaction',
    sourceId: '',
    customFundListObject: {},
    customUnitCodeListObject: {},
    isInterestRateOrAmt: false,
    overrideSource: undefined,
  }),
  created() {
    this.interestRateOrAmt();
  },
  computed: {
    ...mapGetters(['todaysDate']),
    ...mapGetters('base/crud', [
      'items',
      'item',
      'isVisible',
      'criteria',
    ]),
    ...mapState({
      miscSettingItem: (state) => state.SystemConfig.miscSettingItem,
      fiscalYearFromState: (state) => state.glDaily.interestApportionment.fiscalYearForMiscReceipt,
    }),
    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    isVisibleProp: {
      get() {
        return this.isVisible(this.miscBaseCrudKey);
      },
      set(value) {
        this.setIsVisible([this.miscBaseCrudKey, value]);
      },
    },
    editedItem: {
      get() {
        return this.item(this.miscBaseCrudKey);
      },
      set(value) {
        this.setItem([this.miscBaseCrudKey, value]);
      },
    },
    fundWiseInterestDetailsWithTaxUnit() {
      const loadedItem = this.items(this.baseCrudKey);
      if (loadedItem && loadedItem.fundWiseInterestDetailsWithTaxUnits) {
        return loadedItem.fundWiseInterestDetailsWithTaxUnits;
      }
      return [];
    },
    isCriteriaAndMiscFyAreEqual() {
      return this.fiscalYearFromState === this.criteriaState.fiscalYear;
    },
  },
  methods: {
    ...mapMutations('base/crud', [
      'setItem',
      'setIsVisible',
      'setCriteria',
    ]),
    ...mapActions(['flashSuccess']),
    ...mapActions('misc/miscTransaction', [
      'upsertMiscTrans',
      'loadLastReceiptNo',
      'loadLastTransactionNo',
    ]),
    ...mapActions('shared/fund', [
      'loadFunds',
    ]),
    ...mapActions('misc/schoolUnit', [
      'loadMiscUnitCode',
    ]),
    ...mapActions('SystemConfig', ['loadConfig',
    ]),
    async miscNewItem() {
      if (this.isCriteriaAndMiscFyAreEqual) {
        // do nothing
      } else if (this.fiscalYearFromState) {
        // Fund Data Promise
        const fundListPromise = this.loadFunds({
          criteria: {
            fiscalYear: this.fiscalYearFromState,
          },
        });

        // UnitCode Data Promise
        const unitCodeListPromise = this.loadMiscUnitCode({
          criteria: {
            fiscalYear: this.fiscalYearFromState,
          },
        });

        // Fetch Data
        const [fundList, unitCodeList] = await Promise.all([
          fundListPromise,
          unitCodeListPromise,
        ]);

        // Convert Array into Object & Assign Data
        this.customFundListObject = createObjectFromArray(fundList, 'fund');
        this.customUnitCodeListObject = createObjectFromArray(unitCodeList, 'unitCodeNumber');
      }
      this.newItemDialog();
      this.$nextTick(() => {
        this.isVisibleProp = true;
      });
    },
    async interestRateOrAmt() {
      // check interest rate / interest amount
      const glConfig = await this.loadConfig({ requiredScope: 'glConfig' });
      this.overrideSource = ((glConfig || [])[0].configuration.overrideSource) || undefined;
      this.isInterestRateOrAmt = ((glConfig || [])[0].configuration.useRateToCalculateInterest);
    },
    buildMiscDetails() {
      const miscDetails = [];
      let transactionAmount = 0.0;
      if (this.isInterestRateOrAmt) {
        for (let index = 0; index < this.fundWiseInterestDetailsWithTaxUnit.length; index += 1) {
          const currElement = this.fundWiseInterestDetailsWithTaxUnit[index];
          let fundItem;
          let unitCodeItem;
          let taxUnitCode;
          if (this.isCriteriaAndMiscFyAreEqual) {
            fundItem = currElement._id;
            unitCodeItem = currElement.unitCode;
          } else {
            fundItem = (this.customFundListObject[currElement.fundNumber] || {})._id || null;
            // #migrateUnitCode ->remove Unit after migration
            // eslint-disable-next-line max-len
            unitCodeItem = (this.customUnitCodeListObject[currElement.unitCodeNumber] || {})._id || null;
            taxUnitCode = (this.customUnitCodeListObject[currElement.unitCodeNumber] || {})
              .unitCode || null;
          }
          miscDetails.push({
            fund: fundItem,
            source: this.sourceId || null,
            unit: unitCodeItem, // #migrateUnitCode -> remove Unit after migration
            taxDistrict: null,
            percentageBreakdown: 0,
            amount: currElement.interest,
            taxUnitCode,
          });
          transactionAmount += amountToNumber(currElement.interest);
        }
        transactionAmount = amountToNumber(transactionAmount);
      } else {
        for (let index = 0; index < this.fundWiseInterestDetailsWithTaxUnit.length; index += 1) {
          const currElement = this.fundWiseInterestDetailsWithTaxUnit[index];
          let fundItem;
          let unitCodeItem;
          let taxUnitCode;
          if (this.isCriteriaAndMiscFyAreEqual) {
            fundItem = currElement._id;
            unitCodeItem = currElement.unitCode;
          } else {
            fundItem = (this.customFundListObject[currElement.fundNumber] || {})._id || null;
            // #migrateUnitCode ->remove Unit after migration
            // eslint-disable-next-line max-len
            unitCodeItem = (this.customUnitCodeListObject[currElement.unitCodeNumber] || {})._id || null;
            taxUnitCode = (this.customUnitCodeListObject[currElement.unitCodeNumber] || {})
              .unitCode || null;
          }
          miscDetails.push({
            fund: fundItem,
            source: this.sourceId || null,
            unit: unitCodeItem,
            taxDistrict: null,
            percentageBreakdown: 0,
            amount: currElement.interest,
            taxUnitCode,
          });
        }
        transactionAmount = this.interestReceived;
      }
      return {
        miscDetails,
        transactionAmount,
      };
    },
    handleSourceUpdate(updatedSourceObj) {
      const overrideSource = this.overrideSource || GL_CONSTANTS.INTEREST_INCOME_SOURCE;

      const foundObject = updatedSourceObj.find(
        (obj) => obj.sourceSubSourceNumber === overrideSource,
      );
      if (foundObject && (this.sourceId !== foundObject._id)) {
        this.sourceId = foundObject._id;
        this.newItemDialog();
      }
    },
    newItemDialog() {
      const {
        miscDetails,
        transactionAmount,
      } = this.buildMiscDetails();
      const newItem = {
        businessDate: this.todaysDate,
        fiscalYear: this.fiscalYearFromState || toFiscalYear(new Date(this.todaysDate)),
        isApportionLikeTax: false,
        receiptNumber: null,
        isCalculatePercentage: false,
        whomStr: '',
        description: '',
        amount: transactionAmount,
        transactionNumber: null,
        details: miscDetails,
        wkst: getWorkstationNumber(),
        taxYear: this.miscSettingItem.taxYear,
      };
      this.editedItem = { ...newItem };
      return newItem;
    },
    async upsertTransaction(item) {
      const upsertItem = { ...item };
      upsertItem.amount = amountToNumber(upsertItem.amount);
      upsertItem.details = upsertItem.details.map((element) => {
        const amount = amountToNumber(element.amount);
        return { ...element, amount };
      });
      if ((upsertItem.paymentDetails || []).length > 0) {
        const newPaymentDetails = [];
        for (let i = 0; i < upsertItem.paymentDetails.length; i += 1) {
          const element = upsertItem.paymentDetails[i];
          const newUpdatedElement = buildPaymentItem(element);
          // if paymentType is empty, do not push the paymentDetails
          if (newUpdatedElement.paymentType) {
            newPaymentDetails.push({
              ...newUpdatedElement,
            });
          }
        }
        upsertItem.paymentDetails = newPaymentDetails;
      }

      const response = await this.upsertMiscTrans({
        item: upsertItem,
      });
      /* After successfully upsert, get '_id' */
      if (response._id) {
        this.flashSuccess('Misc Receipt has Successfully Save!');
      }
    },
  },
};
</script>
